import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';

const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600&family=Syne:wght@600;700;800&display=swap');

    :root {
      --bg:        #f7f8fa;
      --bg2:       #ffffff;
      --border:    rgba(0,0,0,0.08);
      --border2:   rgba(0,0,0,0.12);
      --dim:       #8a9099;
      --body:      #3d4451;
      --ink:       #0f1117;
      --accent:    #0066ff;
      --accent-bg: rgba(0,102,255,0.06);
      --accent-bd: rgba(0,102,255,0.18);
      --me-bg:     rgba(255,170,0,0.08);
      --me-bd:     rgba(255,170,0,0.35);
      --success:   #10b981;
      --error:     #ef4444;
    }

    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }

    * { box-sizing: border-box; margin: 0; padding: 0; }

    body { background: var(--bg); }

    .lb-row:hover td { background: var(--accent-bg) !important; }
    .lb-row-me td { background: var(--me-bg) !important; }
    .lb-row-me:hover td { background: var(--me-bg) !important; filter: brightness(0.97); }

    @media (max-width: 700px) {
      .lb-table th:nth-child(n+4),
      .lb-table td:nth-child(n+4) { display: none; }
    }
  `}</style>
);

function Nav() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 32px', height: 56,
      background: scrolled ? 'rgba(247,248,250,0.95)' : 'rgba(247,248,250,0.7)',
      backdropFilter: 'blur(16px)',
      borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
      transition: 'border-color 0.3s, background 0.3s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/')}>
        <img src="/logo/scifi_logo_transprent.png" alt="sciFi Logo" style={{ height: 38, width: 'auto' }} />
      </div>

      <button
        type="button"
        onClick={() => navigate('/hackathon')}
        style={{
          fontFamily: 'IBM Plex Mono,monospace', fontSize: 11, letterSpacing: '0.1em',
          textTransform: 'uppercase', padding: '7px 18px',
          background: 'transparent', color: 'var(--ink)',
          border: '1px solid var(--border2)', borderRadius: 3,
          cursor: 'pointer', transition: 'border-color 0.18s',
        }}
      >
        ← Hackathon
      </button>
    </nav>
  );
}

function RankBadge({ rank }) {
  if (rank === 1) return <span style={{ fontSize: 18 }}>🥇</span>;
  if (rank === 2) return <span style={{ fontSize: 18 }}>🥈</span>;
  if (rank === 3) return <span style={{ fontSize: 18 }}>🥉</span>;
  return (
    <span style={{
      fontFamily: 'IBM Plex Mono,monospace', fontWeight: 700,
      fontSize: 13, color: 'var(--dim)',
    }}>
      #{rank}
    </span>
  );
}

function ScoreBar({ value, max = 1 }) {
  const pct = Math.max(0, Math.min(1, value / max)) * 100;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{
        flex: 1, height: 4, background: 'var(--border2)', borderRadius: 2, overflow: 'hidden',
      }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: 'var(--accent)', borderRadius: 2,
          transition: 'width 0.6s ease',
        }} />
      </div>
      <span style={{
        fontFamily: 'IBM Plex Mono,monospace', fontSize: 12,
        fontWeight: 700, color: 'var(--accent)', minWidth: 52, textAlign: 'right',
      }}>
        {(value).toFixed(2)}%
      </span>
    </div>
  );
}

function LiveDot() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%', background: '#10b981',
        display: 'inline-block', animation: 'pulse 1.6s ease-in-out infinite',
      }} />
      <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 10, color: '#10b981', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        Live
      </span>
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function Pagination({ page, totalCount, pageSize, onPage }) {
  const totalPages = Math.ceil(totalCount / pageSize);
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 2;
  const left = Math.max(1, page - delta);
  const right = Math.min(totalPages, page + delta);

  if (left > 1) { pages.push(1); if (left > 2) pages.push('...'); }
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < totalPages) { if (right < totalPages - 1) pages.push('...'); pages.push(totalPages); }

  const btn = (label, target, active = false, disabled = false) => (
    <button
      key={label}
      onClick={() => !disabled && target && onPage(target)}
      disabled={disabled}
      style={{
        fontFamily: 'IBM Plex Mono,monospace', fontSize: 11,
        padding: '5px 10px', minWidth: 34,
        background: active ? 'var(--accent)' : 'transparent',
        color: active ? '#fff' : disabled ? 'var(--border2)' : 'var(--body)',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border2)'}`,
        borderRadius: 3, cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {btn('‹', page - 1, false, page === 1)}
      {pages.map((p, i) =>
        p === '...'
          ? <span key={`ellipsis-${i}`} style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 11, color: 'var(--dim)', padding: '0 4px' }}>…</span>
          : btn(p, p, p === page)
      )}
      {btn('›', page + 1, false, page === totalPages)}
    </div>
  );
}

function MyEntryBanner({ entry, fmt }) {
  return (
    <div style={{
      marginBottom: 16,
      border: '1.5px solid var(--me-bd)',
      borderRadius: 8, overflow: 'hidden',
      background: 'var(--me-bg)',
    }}>
      <div style={{
        padding: '8px 20px',
        borderBottom: '1px solid var(--me-bd)',
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'rgba(255,170,0,0.12)',
      }}>
        <span style={{ fontSize: 14 }}>📍</span>
        <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#b97300', fontWeight: 600 }}>
          Your Standing
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'IBM Plex Mono,monospace' }}>
          <tbody>
            <tr>
              <td style={{ padding: '14px 16px', textAlign: 'center', width: 72 }}>
                <RankBadge rank={entry.rank} />
              </td>
              <td style={{ padding: '14px 16px', color: 'var(--ink)', fontWeight: 700, fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {entry.display_name}
                <span style={{
                  display: 'inline-block', marginLeft: 8,
                  fontFamily: 'IBM Plex Mono,monospace', fontSize: 9,
                  background: '#b97300', color: '#fff',
                  padding: '2px 6px', borderRadius: 2,
                  letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
                }}>You</span>
              </td>
              <td style={{ padding: '14px 16px', minWidth: 180 }}>
                <ScoreBar value={entry.composite_score} max={entry.composite_score} />
              </td>
              <td style={{ padding: '14px 16px', textAlign: 'center', color: 'var(--body)', fontSize: 12 }}>{fmt(entry.roc_auc)}</td>
              <td style={{ padding: '14px 16px', textAlign: 'center', color: 'var(--body)', fontSize: 12 }}>{fmt(entry.pr_auc)}</td>
              <td style={{ padding: '14px 16px', textAlign: 'center', color: 'var(--body)', fontSize: 12 }}>{fmt(entry.f1)}</td>
              <td style={{ padding: '14px 16px', textAlign: 'center', color: 'var(--body)', fontSize: 12 }}>{fmt(entry.recall)}</td>
              <td style={{ padding: '14px 16px', textAlign: 'center', color: 'var(--dim)', fontSize: 11, whiteSpace: 'nowrap' }}>{fmtTime(entry.submitted_at)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

const PAGE_SIZE = 10;

export default function HackathonLeaderboard() {
  const { user, token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalScored, setTotalScored] = useState(0);
  const [myEntry, setMyEntry] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [countdown, setCountdown] = useState(60);
  const timerRef = useRef(null);

  function fetchLeaderboard(p = page) {
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/hackathon/leaderboard`, {
      params: { page: p, page_size: PAGE_SIZE },
    })
      .then(res => {
        setEntries(res.data.entries || []);
        setTotalCount(res.data.total_count || 0);
        setTotalScored(res.data.total_scored || 0);
        setLastUpdated(new Date());
        setError(null);
        setCountdown(60);
      })
      .catch(() => setError('Failed to load leaderboard. Retrying...'))
      .finally(() => setLoading(false));
  }

  function fetchMyRank() {
    if (!token) return;
    axios.get(`${API_BASE_URL}/api/hackathon/my-rank`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => setMyEntry(res.data))
      .catch(() => setMyEntry(null));
  }

  useEffect(() => {
    fetchLeaderboard(1);
    fetchMyRank();
    // const interval = setInterval(() => {
    //   fetchLeaderboard(page);
    //   fetchMyRank();
    // }, 60000);
    // timerRef.current = setInterval(() => setCountdown(c => Math.max(0, c - 1)), 1000);
    // return () => {
    //   clearInterval(interval);
    //   clearInterval(timerRef.current);
    // };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function goToPage(p) {
    setPage(p);
    fetchLeaderboard(p);
    window.scrollTo({ top: 200, behavior: 'smooth' });
  }

  const fmt = v => v != null ? (v*100).toFixed(2) + '%' : '—';
  const maxScore = entries.length > 0 ? entries[0].composite_score : 1;
  const firstRankOnPage = (page - 1) * PAGE_SIZE + 1;

  return (
    <>
      <GlobalStyle />
      <Nav />

      <main style={{
        minHeight: '100vh', background: 'var(--bg)',
        paddingTop: 80, paddingBottom: 80,
      }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 48, paddingTop: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{ width: 20, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
              <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--accent)' }}>
                sciFi Hackathon 2026
              </span>
              <div style={{ width: 20, height: 1.5, background: 'var(--accent)', borderRadius: 1 }} />
            </div>

            <h1 style={{
              fontFamily: 'Syne,sans-serif', fontSize: 'clamp(32px,6vw,60px)',
              fontWeight: 800, color: 'var(--ink)', letterSpacing: '-0.03em',
              marginBottom: 16,
            }}>
              Leaderboard
            </h1>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, flexWrap: 'wrap' }}>
              <LiveDot />
              <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 11, color: 'var(--dim)' }}>
                Best submission per participant
              </span>
              {/* <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 11, color: 'var(--dim)' }}>
                {lastUpdated
                  ? `Updated ${lastUpdated.toLocaleTimeString()} · next in ${countdown}s`
                  : 'Fetching...'}
              </span> */}
            </div>
          </div>

          {/* Stats bar */}
          {!loading && !error && (
            <div style={{
              display: 'flex', gap: 1, marginBottom: 32,
              background: 'var(--border)', borderRadius: 8, overflow: 'hidden',
            }}>
              {[
                { label: 'Participants Scored', value: totalScored },
                { label: 'On Leaderboard', value: totalCount },
                { label: 'Top Score', value: entries.length > 0 && page === 1 ? fmt(entries[0].composite_score) : '—' },
                ...(myEntry ? [{ label: 'Your Rank', value: `#${myEntry.rank}` }] : []),
              ].map(s => (
                <div key={s.label} style={{
                  flex: 1, background: 'var(--bg2)', padding: '20px 24px', textAlign: 'center',
                }}>
                  <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 24, color: 'var(--ink)', marginBottom: 4 }}>
                    {s.value}
                  </div>
                  <div style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 10, color: 'var(--dim)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* My entry pinned banner */}
          {!loading && !error && myEntry && (
            <MyEntryBanner entry={myEntry} fmt={fmt} />
          )}

          {/* Content */}
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
              <div style={{
                width: 32, height: 32,
                border: '3px solid var(--border2)', borderTopColor: 'var(--accent)',
                borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }} />
            </div>
          ) : error ? (
            <div style={{
              background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
              padding: '48px 24px', textAlign: 'center',
            }}>
              <p style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 13, color: 'var(--error)', marginBottom: 16 }}>{error}</p>
              <button
                onClick={() => fetchLeaderboard(page)}
                style={{
                  fontFamily: 'IBM Plex Mono,monospace', fontSize: 11, letterSpacing: '0.1em',
                  textTransform: 'uppercase', padding: '8px 20px',
                  background: 'var(--accent)', color: '#fff',
                  border: 'none', borderRadius: 3, cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          ) : entries.length === 0 ? (
            <div style={{
              background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
              padding: '80px 24px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>🏁</div>
              <p style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 14, color: 'var(--dim)' }}>
                No scored submissions yet — be the first!
              </p>
            </div>
          ) : (
            <div style={{
              background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 8, overflow: 'hidden',
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table className="lb-table" style={{
                  width: '100%', borderCollapse: 'collapse',
                  fontFamily: 'IBM Plex Mono,monospace',
                }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border2)', background: 'var(--bg)' }}>
                      {[
                        { label: 'Rank', align: 'center', w: 72 },
                        { label: 'Participant', align: 'left' },
                        { label: 'Composite Score', align: 'left', w: 200 },
                        { label: 'ROC-AUC', align: 'center', w: 96 },
                        { label: 'PR-AUC', align: 'center', w: 86 },
                        { label: 'F1', align: 'center', w: 72 },
                        { label: 'Recall', align: 'center', w: 80 },
                        { label: 'Submitted', align: 'center', w: 110 },
                      ].map(h => (
                        <th key={h.label} style={{
                          padding: '11px 16px', textAlign: h.align,
                          color: 'var(--dim)', fontWeight: 600,
                          letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 10,
                          whiteSpace: 'nowrap',
                          ...(h.w ? { width: h.w, minWidth: h.w } : {}),
                        }}>
                          {h.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => {
                      const globalRank = firstRankOnPage + i;
                      const isTop3 = globalRank <= 3;
                      const isMe = myEntry && e.rank === myEntry.rank && e.display_name === myEntry.display_name;
                      return (
                        <tr
                          key={e.rank}
                          className={`lb-row${isMe ? ' lb-row-me' : ''}`}
                          style={{
                            borderBottom: '1px solid var(--border)',
                            background: isMe
                              ? 'var(--me-bg)'
                              : isTop3 ? 'var(--accent-bg)' : 'transparent',
                          }}
                        >
                          <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                            <RankBadge rank={globalRank} />
                          </td>
                          <td style={{
                            padding: '14px 16px', color: 'var(--ink)', fontWeight: 600,
                            fontSize: 13, maxWidth: 200,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }} title={e.display_name}>
                            {e.display_name}
                            {isMe && (
                              <span style={{
                                display: 'inline-block', marginLeft: 8,
                                fontFamily: 'IBM Plex Mono,monospace', fontSize: 9,
                                background: '#b97300', color: '#fff',
                                padding: '2px 6px', borderRadius: 2,
                                letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
                              }}>You</span>
                            )}
                            {!isMe && isTop3 && (
                              <span style={{
                                display: 'inline-block', marginLeft: 8,
                                fontFamily: 'IBM Plex Mono,monospace', fontSize: 9,
                                background: 'var(--accent)', color: '#fff',
                                padding: '2px 6px', borderRadius: 2,
                                letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
                              }}>Top 3</span>
                            )}
                          </td>
                          <td style={{ padding: '14px 16px', minWidth: 180 }}>
                            <ScoreBar value={e.composite_score * 100 } max={maxScore} />
                          </td>
                          <td style={{ padding: '14px 16px', textAlign: 'center', color: 'var(--body)', fontSize: 12 }}>{fmt(e.roc_auc)}</td>
                          <td style={{ padding: '14px 16px', textAlign: 'center', color: 'var(--body)', fontSize: 12 }}>{fmt(e.pr_auc)}</td>
                          <td style={{ padding: '14px 16px', textAlign: 'center', color: 'var(--body)', fontSize: 12 }}>{fmt(e.f1)}</td>
                          <td style={{ padding: '14px 16px', textAlign: 'center', color: 'var(--body)', fontSize: 12 }}>{fmt(e.recall)}</td>
                          <td style={{ padding: '14px 16px', textAlign: 'center', color: 'var(--dim)', fontSize: 11, whiteSpace: 'nowrap' }}>
                            {fmtTime(e.submitted_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{
                padding: '12px 20px',
                borderTop: '1px solid var(--border)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexWrap: 'wrap', gap: 12,
                background: 'var(--bg)',
              }}>
                <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 11, color: 'var(--dim)' }}>
                  Showing {firstRankOnPage}–{Math.min(firstRankOnPage + entries.length - 1, totalCount)} of {totalCount} participant{totalCount !== 1 ? 's' : ''}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Pagination page={page} totalCount={totalCount} pageSize={PAGE_SIZE} onPage={goToPage} />
                  <button
                    onClick={() => { fetchLeaderboard(page); fetchMyRank(); }}
                    style={{
                      fontFamily: 'IBM Plex Mono,monospace', fontSize: 10, letterSpacing: '0.1em',
                      textTransform: 'uppercase', padding: '6px 14px',
                      background: 'transparent', color: 'var(--accent)',
                      border: '1px solid var(--accent-bd)', borderRadius: 3,
                      cursor: 'pointer',
                    }}
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Metric legend */}
          <div style={{
            marginTop: 32, padding: '24px 28px',
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
          }}>
            <p style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 10, color: 'var(--dim)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 14 }}>
              Scoring Breakdown
            </p>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {[
                { label: 'ROC-AUC', weight: '40%' },
                { label: 'PR-AUC', weight: '30%' },
                { label: 'F1-Score', weight: '15%' },
                { label: 'Recall', weight: '15%' },
              ].map(m => (
                <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 3, height: 16, background: 'var(--accent)', borderRadius: 2 }} />
                  <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 12, color: 'var(--body)' }}>
                    {m.label}
                  </span>
                  <span style={{ fontFamily: 'IBM Plex Mono,monospace', fontSize: 11, color: 'var(--dim)' }}>
                    {m.weight}
                  </span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </main>
    </>
  );
}
